
import { UserProfile } from "@/types/profile";
import { Calendar, Clock, Music, Sparkles } from "lucide-react";
import { useState, useEffect } from "react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { glassCard } from "@/lib/theme-utils";

type WelcomeHeaderProps = {
  profile: UserProfile | null;
};

export const WelcomeHeader = ({ profile }: WelcomeHeaderProps) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const isMobile = useMediaQuery("(max-width: 768px)");
  
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  
  const timeOfDay = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    if (hour < 22) return 'evening';
    return 'night';
  };

  const getMoodPhrase = () => {
    const hour = currentTime.getHours();
    if (hour < 10) return "Time to plan your day!";
    if (hour < 16) return "What's your move tonight?";
    if (hour < 20) return "The night is calling!";
    return "Let's get this party started!";
  };

  const formattedDate = currentTime.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });

  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  return (
    <div className="glass-card p-4 md:p-6 rounded-2xl relative overflow-hidden group animate-slide-up">
      {/* Enhanced background effects */}
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 blur-3xl -translate-y-1/2 translate-x-1/2 animate-float"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-gradient-to-tr from-accent/20 to-primary/20 blur-3xl translate-y-1/2 -translate-x-1/2 animate-float" style={{ animationDelay: '1s' }}></div>
      
      {/* Gradient border highlight */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/20 via-transparent to-accent/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      <div className="relative z-10">
        {isMobile ? (
          <div className="space-y-3">
            <h1 className="text-2xl font-heading font-bold group transition-all duration-500 flex items-center gap-2">
              <span className="animate-slide-up">Good {timeOfDay()},</span>
              <span className="text-gradient animate-text-shimmer bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent bg-size-200 font-black">
                {profile?.first_name || 'Night Owl'}
              </span>
              <Sparkles className="h-5 w-5 text-primary animate-bounce-gentle" />
            </h1>
            <p className="text-sm text-muted-foreground font-medium animate-slide-up" style={{ animationDelay: '0.1s' }}>
              {getMoodPhrase()}
            </p>
            <div className="flex items-center gap-4 text-muted-foreground text-xs mt-3 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center gap-1.5 bg-white/5 px-2 py-1 rounded-full">
                <Calendar className="w-3 h-3 text-primary" />
                <span className="font-medium">{formattedDate}</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/5 px-2 py-1 rounded-full">
                <Clock className="w-3 h-3 text-secondary" />
                <span className="font-medium">{formattedTime}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <h1 className="text-4xl font-heading font-bold group transition-all duration-500 flex items-center gap-3">
                <span className="animate-slide-up">Good {timeOfDay()},</span>
                <span className="text-gradient animate-text-shimmer bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent bg-size-200 font-black">
                  {profile?.first_name || 'Night Owl'}
                </span>
                <Sparkles className="h-6 w-6 text-primary animate-bounce-gentle" />
              </h1>
              <p className="text-muted-foreground text-lg font-medium animate-slide-up" style={{ animationDelay: '0.1s' }}>
                {getMoodPhrase()}
              </p>
            </div>
            
            <div className="flex flex-col gap-2 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-full">
                <Calendar className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{formattedDate}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-full">
                <Clock className="w-4 h-4 text-secondary" />
                <span className="text-sm font-medium">{formattedTime}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
